// src/components/DashboardPanel.jsx
import React, { useState, useEffect } from 'react';
import SharedCard from './SharedCard';

const mxn = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN',
  maximumFractionDigits: 0 }).format(Number(n) || 0);

const Bar = ({ value, max, color = 'bg-[#961914]' }) => (
  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
    <div className={`${color} h-1.5 rounded-full transition-all duration-500`}
      style={{ width: max > 0 ? `${(value / max) * 100}%` : '0%' }} />
  </div>
);

// FIX: texto reducido para móvil — text-2xl en lugar de text-4xl, truncate en valores
const MetricCard = ({ label, value, sub, gradient }) => (
  <div className={`${gradient} px-3 py-4 rounded-xl shadow-lg text-white flex flex-col items-center justify-center`}>
    <p className="text-xs uppercase tracking-wide opacity-75 mb-1 text-center leading-tight">{label}</p>
    <p className="text-xl sm:text-2xl font-extrabold text-center leading-tight w-full truncate px-1">{value}</p>
    {sub && <p className="text-xs opacity-70 mt-1 text-center">{sub}</p>}
  </div>
);

const DashboardPanel = ({ currentUser }) => {
  const [studentsList, setStudentsList] = useState(null);
  const [salesList,    setSalesList]    = useState(null);
  const [productsList, setProductsList] = useState(null);
  const [teachersList, setTeachersList] = useState(null);
  const [period,       setPeriod]       = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [stuR, salR, prodR, teaR] = await Promise.all([
          fetch('/api/list_students.php',  { credentials: 'include' }),
          fetch('/api/list_sales.php',     { credentials: 'include' }),
          fetch('/api/list_products.php',  { credentials: 'include' }),
          fetch('/api/list_teachers.php',  { credentials: 'include' }),
        ]);
        const [stuJ, salJ, prodJ, teaJ] = await Promise.all([
          stuR.json(), salR.json(), prodR.json(), teaR.json()
        ]);
        setStudentsList(Array.isArray(stuJ)  ? stuJ  : []);
        setSalesList(   Array.isArray(salJ)  ? salJ  : []);
        setProductsList(Array.isArray(prodJ) ? prodJ : []);
        setTeachersList(Array.isArray(teaJ)  ? teaJ  : []);
      } catch {
        setStudentsList([]); setSalesList([]);
        setProductsList([]); setTeachersList([]);
      }
    };
    load();
  }, []);

  const parseDate = str => {
    if (!str || typeof str !== 'string') return null;
    const parts = str.split('/');
    if (parts.length === 3) {
      const [d, m, y] = parts;
      return new Date(`${y}-${m}-${d}`);
    }
    return new Date(str);
  };

  const isInPeriod = dateStr => {
    const dt = parseDate(dateStr);
    if (!dt || isNaN(dt)) return false;
    const now = new Date();
    if (period === 'week') {
      const weekAgo = new Date(); weekAgo.setDate(now.getDate() - 7);
      return dt >= weekAgo;
    }
    if (period === 'month') {
      return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
    }
    return true;
  };

  if (studentsList === null || salesList === null || productsList === null || teachersList === null) {
    return (
      <SharedCard title="Dashboard">
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-10 h-10 border-4 border-[#961914] border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Cargando dashboard…</p>
        </div>
      </SharedCard>
    );
  }

  const activeStudents   = studentsList.filter(s => (s.status || '').toLowerCase() === 'activo');
  const inactiveStudents = studentsList.filter(s => (s.status || '').toLowerCase() !== 'activo');
  const filteredSales    = salesList.filter(s => isInPeriod(s.date));
  const totalSalesAmount = filteredSales.reduce((sum, s) => sum + Number(s.total || 0), 0);
  const totalDisciplines = productsList.filter(p => p.type === 'discipline').length;

  const salesByMonth = salesList.reduce((acc, sale) => {
    const dt = parseDate(sale.date);
    if (!dt || isNaN(dt)) return acc;
    const key = dt.toLocaleString('es-MX', { month: 'short', year: 'numeric' });
    acc[key] = (acc[key] || 0) + Number(sale.total || 0);
    return acc;
  }, {});

  const sortedMonths = Object.entries(salesByMonth)
    .sort(([a], [b]) => {
      const toD = s => new Date(s.replace(/(\w+)\s(\d+)/, '$1 1, $2'));
      return toD(a) - toD(b);
    }).slice(-6);

  const productCounts = {};
  filteredSales.forEach(sale => {
    if (!Array.isArray(sale.products)) return;
    sale.products.forEach(prod => {
      productCounts[prod.name] = (productCounts[prod.name] || 0) + Number(prod.quantity || 0);
    });
  });
  const topProducts = Object.entries(productCounts).sort(([,a],[,b]) => b - a).slice(0, 5);
  const maxProd = topProducts[0]?.[1] || 1;

  const discCounts = {};
  activeStudents.forEach(stu => {
    const disc = stu.discipline || '—';
    discCounts[disc] = (discCounts[disc] || 0) + 1;
  });
  const discEntries = Object.entries(discCounts).sort(([,a],[,b]) => b - a);
  const maxDisc = discEntries[0]?.[1] || 1;

  const avgMonthly = sortedMonths.length > 0
    ? sortedMonths.reduce((s, [,v]) => s + v, 0) / sortedMonths.length : 0;

  const periodLabel = { all: 'Histórico', month: 'Este mes', week: 'Esta semana' };

  return (
    <SharedCard title={currentUser?.academy_name || 'Dashboard'}>

      {/* Selector de período */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {['all','month','week'].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${
              period === p ? 'bg-[#961914] text-white shadow' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {periodLabel[p]}
          </button>
        ))}
      </div>

      {/* FIX: grid 2 cols en móvil, 4 en desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard
          label="Estudiantes activos"
          value={activeStudents.length}
          sub={`${inactiveStudents.length} inactivos`}
          gradient="bg-gradient-to-br from-[#961914] to-red-700"
        />
        <MetricCard
          label={`Ventas ${periodLabel[period]}`}
          value={mxn(totalSalesAmount)}
          sub={`${filteredSales.length} ventas`}
          gradient="bg-gradient-to-br from-gray-700 to-gray-900"
        />
        <MetricCard
          label="Disciplinas"
          value={totalDisciplines}
          sub="en catálogo"
          gradient="bg-gradient-to-br from-blue-600 to-blue-800"
        />
        <MetricCard
          label="Maestros"
          value={teachersList.length}
          sub="registrados"
          gradient="bg-gradient-to-br from-green-600 to-green-800"
        />
      </div>

      {/* Promedio mensual — fila completa en móvil */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col items-center mb-5">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Promedio mensual</p>
        <p className="text-2xl font-extrabold text-[#961914]">{mxn(avgMonthly)}</p>
        <p className="text-xs text-gray-400 mt-1">últimos {sortedMonths.length} meses</p>
      </div>

      {/* Top productos + Ventas por mes — apilados en móvil */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-black mb-3">Productos más vendidos</h3>
          {topProducts.length === 0 ? (
            <p className="text-gray-400 text-sm">Sin ventas en este período.</p>
          ) : (
            <ul className="space-y-2">
              {topProducts.map(([name, qty]) => (
                <li key={name}>
                  <div className="flex justify-between text-xs gap-2">
                    <span className="font-medium text-black truncate">{name}</span>
                    <span className="font-bold text-blue-600 flex-shrink-0">{qty} uds.</span>
                  </div>
                  <Bar value={qty} max={maxProd} color="bg-blue-500" />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-black mb-3">Ventas por mes (últimos 6)</h3>
          {sortedMonths.length === 0 ? (
            <p className="text-gray-400 text-sm">Sin datos de ventas.</p>
          ) : (
            <ul className="space-y-2">
              {sortedMonths.map(([month, amount]) => {
                const maxAmt = Math.max(...sortedMonths.map(([,v]) => v));
                return (
                  <li key={month}>
                    <div className="flex justify-between text-xs gap-2">
                      <span className="font-medium text-black capitalize">{month}</span>
                      <span className="font-bold text-[#961914] flex-shrink-0">{mxn(amount)}</span>
                    </div>
                    <Bar value={amount} max={maxAmt} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Estudiantes por disciplina */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-black mb-3">Estudiantes activos por disciplina</h3>
        {discEntries.length === 0 ? (
          <p className="text-gray-400 text-sm">Sin datos de disciplinas.</p>
        ) : (
          <ul className="space-y-2">
            {discEntries.map(([disc, count]) => (
              <li key={disc}>
                <div className="flex justify-between text-xs gap-2">
                  <span className="font-medium text-black truncate">{disc}</span>
                  <span className="font-bold text-green-600 flex-shrink-0">{count} alumnos</span>
                </div>
                <Bar value={count} max={maxDisc} color="bg-green-500" />
              </li>
            ))}
          </ul>
        )}
      </div>

    </SharedCard>
  );
};

export default DashboardPanel;